import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { pillarArticles } from '@/mocks/articles';
import { useSeo, FAVICON_URL } from '@/lib/seo';
import {
  useJsonLd,
  buildArticleSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
} from '@/lib/schema';

export default function Guide() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = pillarArticles.find((a) => a.slug === slug);

  useSeo(
    article
      ? {
          title: `${article.title} | AllChinaBuy Spreadsheet`,
          description: article.excerpt,
          keywords: `AllChinaBuy, AllChinaBuy Spreadsheet, ${article.tag}`,
          path: `/guides/${article.slug}`,
          type: 'article',
          image: FAVICON_URL,
        }
      : {
          title: 'Guide Not Found | AllChinaBuy Spreadsheet',
          description: 'The guide you are looking for could not be found.',
          path: '/guides',
          robots: 'noindex, nofollow',
        }
  );

  useJsonLd(
    article
      ? [
          buildArticleSchema({
            title: article.title,
            description: article.excerpt,
            path: `/guides/${article.slug}`,
            datePublished: '2026-01-01',
            dateModified: '2026-08-24',
          }),
          buildFaqSchema(article.faqs),
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Shopping Guide', path: '/allchinabuy-spreadsheet' },
            { name: article.tag, path: `/guides/${article.slug}` },
          ]),
        ]
      : null
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-28 pb-20">
          <div className="mx-auto max-w-2xl px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {t('guide.notFound')}
            </h1>
            <p className="mt-3 text-sm text-foreground-600">{t('guide.notFoundDesc')}</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('common.backHome')}
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = pillarArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-24 pb-10">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">{t('nav.home')}</Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <Link to="/#guides" className="hover:text-primary-700 cursor-pointer">{t('pillar.title')}</Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <span className="text-foreground-700">{article.tag}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-xs font-semibold text-accent-900">
              <i className="ri-article-line w-4 h-4 flex items-center justify-center" />
              {article.tag}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold leading-tight text-foreground-950">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center" />
                {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center" />
                {t('article.updated')}: August 2026
              </span>
            </div>
          </header>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <p className="text-sm font-semibold text-foreground-900 mb-3">Table of Contents</p>
            <ul className="space-y-1.5 text-sm text-foreground-600">
              {article.sections.map((s, i) => (
                <li key={i}>
                  <a href={`#section-${i}`} className="hover:text-primary-700 cursor-pointer">
                    {i + 1}. {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <article className="mt-8 space-y-8 text-foreground-800 leading-relaxed">
            {article.intro.map((p, i) => (
              <p key={i} className="text-base md:text-lg text-foreground-700">
                {i === 0 ? (
                  <>
                    {p} <Link to="/" className="text-primary-600 underline">Back to our home page</Link> to browse every category.
                  </>
                ) : (
                  p
                )}
              </p>
            ))}

            {article.sections.map((section, i) => (
              <section key={i} id={`section-${i}`}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{section.heading}</h2>
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="mt-3 text-sm md:text-base">{p}</p>
                ))}
              </section>
            ))}

            <p className="mt-4 text-sm text-foreground-500 italic">
              This is an independent informational resource and is not affiliated with or operated by AllChinaBuy.
            </p>
          </article>

          <div className="mt-10 rounded-lg bg-primary-600 p-6 md:p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">{t('guide.ctaTitle')}</h2>
            <p className="mt-2 text-sm text-background-100/90">{t('guide.ctaDesc')}</p>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('hero.ctaPrimary')}
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('common.backHome')}
              </Link>
            </div>
          </div>

          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {article.faqs.map((f) => (
                <div key={f.q} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900">{f.q}</h3>
                  <p className="mt-2 text-sm text-foreground-600">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10 border-t border-background-200 pt-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">{t('guide.related')}</h2>
            <div className="grid grid-cols-1 gap-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/guides/${r.slug}`}
                  className="group flex items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 px-5 py-4 hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="min-w-0">
                    <span className="text-xs font-semibold text-accent-700">{r.tag}</span>
                    <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors truncate">{r.title}</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 w-5 h-5 shrink-0 flex items-center justify-center" />
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}